/**
 * Aleatoriedade determinística (§10, ADR-0004).
 *
 * Toda escolha do jogo é função pura de
 * `(semente da sessão, chave da célula)`.
 * Não existe estado global: desfazer (§11) e restaurar a sessão (§12)
 * reproduzem exatamente a mesma sequência de sugestões.
 *
 * `Math.random` é proibido em `src/core` pelo ESLint (ver eslint.config.js).
 */

export type Semente = number;

const FNV_OFFSET = 0x811c9dc5;
const FNV_PRIME = 0x01000193;

/** FNV-1a de 32 bits. Estável entre plataformas e entre execuções. */
export function sementeDeTexto(texto: string): Semente {
  let h = FNV_OFFSET;
  for (let i = 0; i < texto.length; i += 1) {
    h ^= texto.charCodeAt(i);
    h = Math.imul(h, FNV_PRIME);
  }
  return h >>> 0;
}

/** Combina uma semente com partes adicionais, preservando a ordem. */
export function derivar(semente: Semente, ...partes: readonly (string | number)[]): Semente {
  let h = semente >>> 0;
  for (const parte of partes) {
    const texto = typeof parte === 'number' ? parte.toString(36) : parte;
    h ^= sementeDeTexto(texto);
    h = Math.imul(h, FNV_PRIME);
  }
  return h >>> 0;
}

/** mulberry32: um passo do gerador. Devolve valor em [0, 1) e o próximo estado. */
export function proximo(estado: Semente): { readonly valor: number; readonly estado: Semente } {
  const t = (estado + 0x6d2b79f5) >>> 0;
  let x = t;
  x = Math.imul(x ^ (x >>> 15), x | 1);
  x ^= x + Math.imul(x ^ (x >>> 7), x | 61);
  const valor = ((x ^ (x >>> 14)) >>> 0) / 4294967296;
  return { valor, estado: t };
}

/** Inteiro em [0, limite). Devolve 0 quando `limite <= 0`. */
export function inteiroAte(semente: Semente, limite: number): number {
  if (limite <= 0) return 0;
  return Math.floor(proximo(semente).valor * limite) % limite;
}

/** Escolhe um item de forma determinística. */
export function escolher<T>(semente: Semente, itens: readonly T[]): T | undefined {
  if (itens.length === 0) return undefined;
  return itens[inteiroAte(semente, itens.length)];
}

/**
 * Fisher-Yates determinístico. Usado para percorrer candidatos numa ordem
 * estável porém não alfabética.
 */
export function embaralhar<T>(semente: Semente, itens: readonly T[]): T[] {
  const saida = itens.slice();
  let estado = semente;
  for (let i = saida.length - 1; i > 0; i -= 1) {
    const passo = proximo(estado);
    estado = derivar(passo.estado, i);
    const j = Math.floor(passo.valor * (i + 1));
    const a = saida[i];
    const b = saida[j];
    if (a === undefined || b === undefined) continue;
    saida[i] = b;
    saida[j] = a;
  }
  return saida;
}

/**
 * Semente das sugestões de uma célula (§10, T03-05): a mesma sessão e a mesma
 * célula produzem sempre as mesmas verticais, inclusive depois de desfazer ou
 * de recarregar a página.
 */
export function sementeDaCelula(sementeSessao: Semente, chaveDaCelula: string): Semente {
  return derivar(sementeSessao, 'sugestao', chaveDaCelula);
}

/**
 * Gera a semente de uma nova sessão. É o único ponto do projeto que consome
 * entropia real; o valor é persistido junto do estado (§12).
 */
export function novaSemente(fonteDeEntropia: () => number): Semente {
  return Math.floor(fonteDeEntropia() * 0xffffffff) >>> 0;
}
